import React, { ReactElement, useCallback, useEffect, useState } from "react";
// import { useTranslation } from "react-i18next";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import { getFormErrorByName, incomeAttributeMatcher, MatchingStrategy, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { formatCurrency } from "../../../utils/formatter";
import "./CurrencyInput.css"

export interface CurrencyInputProps {
    id?: string,
    name?: string,
    currency?: string,
    value?: number | null | undefined,
    onChange?: (newAmt: number | undefined | null, name: string, oldAmt: number | undefined | null) => void,
    errormessage?: string | null | undefined,
    symbol?: string | React.ReactElement,
    className?: string,
    placeholder?: string,
    title?: string,
    readOnly?: boolean,
    allowNegative?: boolean,
    disabled?: boolean,
    forceEnabled?: boolean,
    errorMatcher?: MatchingStrategy,
    testid?: string,
}

function CurrencyInput(props: CurrencyInputProps): ReactElement {
    const { removeError, errors } = useFormErrorContext();
    const { isReadOnly } = useReadOnly()
    // PIER2-989: For now, the English and French currency should be displayed the same English format.
    // const { i18n } = useTranslation();
    const lang = "en"

    const elementClassName = "currency-container" + (props.className ? " " + props.className : "")
    const matcher = props.errorMatcher ? props.errorMatcher : incomeAttributeMatcher
    const name = props.name ? props.name : (props.id ? props.id : new Date().getTime().toString());

    const [value, setValue] = useState(props.value)
    const [error, setError] = useState('')
    const [localeGroupSeparator, setLocaleGroupSeparator] = useState<string>(",")
    const [separators, setSeparators] = useState<Record<string, string>>({})
    const [allowedInputChars, setAllowedInputChars] = useState<string[]>([])

    const formatValue = useCallback((value: number | null | undefined) => {
        let formattedValue = formatCurrency(value, lang)
        if (formattedValue == null || formattedValue === undefined) {
            return "";
        }
        if (localeGroupSeparator !== separators.group) {
            formattedValue = formattedValue.replaceAll(localeGroupSeparator, separators.group)
        }
        return formattedValue
    }, [localeGroupSeparator, separators.group]);

    const [displayValue, setDisplayValue] = useState(formatValue(props.value));

    const getSeparator = (locale: string, separatorType: string) => {
        const numberWithGroupAndDecimalSeparator = -10000.1;
        return Intl.NumberFormat(locale).formatToParts(numberWithGroupAndDecimalSeparator).find(part => part.type === separatorType)?.value;
    }

    const filterAllowedInputChars = (input: string) => {
        const filterInputCharArray = []
        for (const char of input) {
            if (allowedInputChars.includes(char)) {
                filterInputCharArray.push(char)
            }
        }
        const join = filterInputCharArray.join("")
        return join
    }

    const cleanToNumberFormat = (value: string) => {
        const cleanCharArray = []
        for (const char of value) {
            if (allowedInputChars.includes(char)) {
                if (char === separators.decimal) {
                    cleanCharArray.push(".")
                } else if (char === separators.group) {
                    // discard group separator
                } else {
                    cleanCharArray.push(char)
                }
            }
        }
        const cleaned = cleanCharArray.join("")
        return cleaned
    }

    const isValidAfterClean = (cleaned: string, original: string) => {
        const numCleaned = Number(cleaned)
        if (isNaN(numCleaned)) {
            return false
        }
        if (numCleaned === 0) {
            const numOriginal = Number(original)
            if (isNaN(numOriginal)) {
                return false
            }
            if (cleaned === "" && original === "") {
                return true
            }
        }
        return true
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        removeError(getFormErrorByName(errors, props.id as string)?.primaryKey as string)
        removeError(getFormErrorByName(errors, props.id as string, matcher)?.primaryKey as string)

        const targetValue = e.target.value;

        const display = filterAllowedInputChars(targetValue);
        setDisplayValue(display)
        const numberFormat = cleanToNumberFormat(display);
        const targetNumber = Number(numberFormat)
        isNaN(targetNumber) ? setError("Invalid number") : setError("");
        // e.stopPropagation();
    }

    const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        const targetValue = e.target.value.trim()
        const cleaned = cleanToNumberFormat(targetValue)
        if (isValidAfterClean(cleaned, targetValue)) {
            let newValue = undefined
            if (cleaned !== "") {
                const valueParts = Intl.NumberFormat(lang, { maximumFractionDigits: 2 })
                    .formatToParts(Number(cleaned))
                const filteredPart = valueParts.filter(part => part.type !== 'group')
                const decimalPart = filteredPart.find(part => part.type === 'decimal')
                if (decimalPart) {
                    decimalPart.value = '.'
                }
                const newValueString = (filteredPart.map((part) => part.value)).join("")
                newValue = Number(newValueString)
            }
            setValue(newValue)
            setDisplayValue(formatValue(newValue))
            if (props.onChange) {
                props.onChange(newValue, name, value)
            }
        } else {
            setDisplayValue(formatValue(value))
            setError("")
        }
    }

    useEffect(() => {
        const getGroupChar = (groupChar: string) => {
            if (groupChar.charCodeAt(0) === 8239) {
                return " ";
            } else {
                return groupChar;
            }
        }
        const initSeparators = () => {
            const groupSeparator = getSeparator(lang, 'group');
            setLocaleGroupSeparator(groupSeparator as string);
            const separators: Record<string, string> = {
                decimal: getSeparator(lang, 'decimal') as string,
                group: getGroupChar(groupSeparator as string) as string,
            }
            if (props.allowNegative !== false) {
                separators["minus"] = getSeparator(lang, 'minusSign') as string;
            }
            setSeparators(separators);
            setAllowedInputChars(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].concat(Object.values(separators)));
        }

        initSeparators();
        setDisplayValue(formatValue(value));
    }, [formatValue, props.allowNegative, value]);

    useEffect(() => {
        setValue(props.value)
        setDisplayValue(formatValue(props.value))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value])

    return (
        <div className={(props.disabled ? "disabled-currency-wrapper" : "currency-wrapper")}>
            <div id={name + "-container"}
                className={elementClassName}>
                {
                    // lang !== "fr" &&
                    <div className="currency-symbol">
                        {props.symbol ? props.symbol : "$"}
                    </div>
                }

                <input {...props} type="text"
                    id={name} name={name} aria-label={name}
                    className={"currency-input"}
                    onChange={(e) => onChange(e)}
                    onBlur={(e) => onBlur(e)}
                    value={displayValue}
                    autoComplete="off"
                    disabled={props.forceEnabled ? false : (isReadOnly || props.disabled || props.readOnly)}
                    maxLength={20}
                    data-primary-key={getFormErrorByName(errors, props.id as string, matcher)?.primaryKey as string}
                    data-testid={props.testid}
                />

                {
                    // lang === "fr" &&
                    // <div className="currency-symbol">
                    //     {props.symbol ? (props.symbol) : "$"}
                    // </div>
                }
            </div>
            {(error || props.errormessage) && (
                <p id={name + "-error"} className="form-error-message">
                    {error === "" ? props.errormessage : error}
                </p>
            )}
        </div>
    )
}

export default CurrencyInput;
